import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { QueryPatternViewStore } from "../../store";
import { QueryPatternView } from "../view/QueryPatternView";
import { QueryPatternUpdateStore } from "../../store";
import { QueryPatternUpdate } from "../update/QueryPatternUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type QueryPatternModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const QueryPatternModalContent: React.FC<QueryPatternModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new QueryPatternUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new QueryPatternViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <QueryPatternView
            queryPatternViewStore={storeInfo.store as QueryPatternViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <QueryPatternUpdate
            queryPatternUpdateStore={storeInfo.store as QueryPatternUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
